import { Pencil, Undo2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { frameLabel } from '@/lib/frame-label';
import type { CorrectionReason } from '@/lib/api/types';
import { CORRECTION_REASONS, STAGE_LABEL, editableValue, humanise, type AttributeView } from './review-model';

interface AttributeRowProps {
  attribute: AttributeView;
  assetKeys: string[];
  /** The draft value, when the row is being edited or filled. */
  draft: string | undefined;
  reason: CorrectionReason | undefined;
  disabled: boolean;
  onDraft: (value: string | undefined) => void;
  onReason: (reason: CorrectionReason | undefined) => void;
}

/**
 * One attribute of the item standard, as one row of the Fields tab
 * (wireframe R-A): the label and the frame it was read off, the value, a
 * badge, and Edit. The badge is the confidence for a machine reading and
 * "Person" for a value someone has verified. An empty attribute is an input
 * from the start, so the operator fills it where it sits. The reason chips
 * under a changed value are optional: a correction without a reason is
 * still a correction.
 */
export function AttributeRow({ attribute, assetKeys, draft, reason, disabled, onDraft, onReason }: AttributeRowProps) {
  const { name, field } = attribute;
  const label = humanise(name);
  const frame = field ? frameLabel(field, assetKeys) : null;
  const editing = draft !== undefined;
  const alwaysInput = !field;
  const inputId = `attribute-${name}`;
  const changed = editing && (field ? draft !== editableValue(field) : draft.trim() !== '');

  const badge = attribute.missingRequired ? (
    <Badge tone="warning">Required</Badge>
  ) : !field ? (
    <Badge>Empty</Badge>
  ) : field.verified ? (
    <Badge tone="success">Person</Badge>
  ) : (
    <Badge tone={attribute.lowConfidence ? 'warning' : 'success'} title={STAGE_LABEL[field.stage]}>
      {field.confidence !== undefined ? `${Math.round(field.confidence * 100)}%` : STAGE_LABEL[field.stage]}
    </Badge>
  );

  return (
    <li className={cn('flex flex-col gap-2 border-b border-border-strong py-2', attribute.missingRequired && 'border-b-warning')}>
      <div className="grid min-h-11 grid-cols-[minmax(0,1fr)_auto_2.75rem] items-center gap-2">
        <div className="flex min-w-0 flex-col">
          <label htmlFor={editing || alwaysInput ? inputId : undefined} className="truncate text-xs text-muted">
            {label}
            {frame && ` · ${frame}`}
          </label>
          {editing || alwaysInput ? (
            <Input
              id={inputId}
              value={draft ?? ''}
              disabled={disabled}
              placeholder={label}
              className="mt-1"
              onChange={(event) => onDraft(event.target.value)}
            />
          ) : (
            <span className="break-words text-sm">{editableValue(field)}</span>
          )}
        </div>
        {badge}
        {field && editing ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={`Undo the change to ${label}`}
            disabled={disabled}
            onClick={() => {
              onDraft(undefined);
              onReason(undefined);
            }}
          >
            <Undo2 aria-hidden="true" />
          </Button>
        ) : field ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={`Edit ${label}`}
            disabled={disabled}
            onClick={() => onDraft(editableValue(field))}
          >
            <Pencil aria-hidden="true" />
          </Button>
        ) : (
          <span aria-hidden="true" />
        )}
      </div>

      {field?.machineValue && (
        <p className="text-xs text-muted">
          Machine: &ldquo;{String(field.machineValue.value ?? 'nothing')}&rdquo;
        </p>
      )}

      {changed && (
        <fieldset className="flex flex-col gap-1.5">
          <legend className="mb-1 text-xs text-muted">Reason</legend>
          <div className="flex flex-wrap gap-2">
            {CORRECTION_REASONS.map((option) => {
              const selected = reason === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected}
                  disabled={disabled}
                  onClick={() => onReason(selected ? undefined : option.value)}
                  className={cn(
                    'min-h-11 rounded-control border px-3 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                    selected ? 'border-accent bg-accent/20 text-surface-foreground' : 'border-border-strong text-muted',
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}
    </li>
  );
}
